import { Controller } from "@hotwired/stimulus";
import Decimal from "decimal.js";
import Highcharts from "highcharts";
import Swiper from "swiper";
import { Autoplay, EffectFade, Navigation, Pagination } from "swiper/modules";

import HighchartsMoreFn from "highcharts/highcharts-more";
import HighchartsAccessibilityFn from "highcharts/modules/accessibility";
HighchartsMoreFn(Highcharts);
HighchartsAccessibilityFn(Highcharts);

let count = 0;
// Connects to data-controller="pie-chart"
export default class extends Controller {
  static values = {
    id: String,
    isMobile: Boolean,
    height: String,
    width: String,
    currentTime: String,
  };
  static targets = [
    "trustEstatePieChart",
    "countryPieChart",
    "industryPieChart",
    "tpxindustryPieChart",
    "trustEstatePieTable",
    "countryPieTable",
    "industryPieTable",
    "tpxindustryPieTable",
    "detailedInfoMemberModal",
  ];

  connect() {
    document.addEventListener("loadPieChart", () => {
      this.loadChart()
    })
  }

  loadChart() {
    this.getPieChartData(this.currentTimeValue)
  }

  async getPieChartData(cur_ym) {
    let url = "/json/funds/" + this.idValue + "/pie_type.json?cur_ym=" + cur_ym;
    await fetch(url, { method: "GET" })
      .then((result) => result.json())
      .then((data) => {
        let trust_values = Object.values(data.data.trustproperty);
        let country_values = Object.values(data.data.st_isocountryjp);
        let industry_values = Object.values(data.data.st_incindustryname);
        let tpxindustry_values = Object.values(data.data.st_tpxindustryname);

        this.createPieChart(this.trustEstatePieChartTarget, trust_values[1]);
        this.createPieChart(this.countryPieChartTarget, country_values[1]);
        this.createPieChart(this.industryPieChartTarget, industry_values[1]);
        this.createPieChart(
          this.tpxindustryPieChartTarget,
          tpxindustry_values[1]
        );
        this.dealTable(this.trustEstatePieTableTarget, trust_values[1]);
        this.dealTable(this.countryPieTableTarget, country_values[1]);
        this.dealTable(this.industryPieTableTarget, industry_values[1]);
        this.dealTable(this.tpxindustryPieTableTarget, tpxindustry_values[1]);
      })
      .catch((errorMsg) => {
        console.log(errorMsg);
      });
  }

  createPieChart(container, data) {
    if (
      container.parentElement
        .querySelector(".chart_div")
        .querySelector(".chart_ul_box")
    ) {
      container.parentElement
        .querySelector(".chart_div")
        .removeChild(".chart_ul_box");
    }
    let sliceColors = [
      "#0168b3",
      "#66b7ec",
      "#82c0cd",
      "#4155b7",
      "#b158db",
      "#9997f7",
      "#e086ea",
      "#e4c4db",
      "#6868b5",
      "#9fb3ed",
      "#5a89ff",
      "#634a96",
      "#0aaeef",
      "#0882bf",
      "#20cee5",
      "#be91bc",
      "#bfbff7",
      "#f995eb",
      "#ea536c",
      "#f9cddd",
      "#f7cdf3",
      "#ef7f2b",
      "#f9caa7",
      "#f7cc19",
      "#c6db8a",
      "#b8ed72",
      "#f7e87c",
      "#b8eaa4",
      "#afd0ef",
      "#845d9e",
      "#efa8e8",
      "#b65eef",
      "#7ebef2",
      "#e55c5c",
      "#f9caca",
      "#b4daef",
    ];
    if (data) {
      if (data.length > sliceColors.length) {
        let len = data.length - sliceColors.length;
        for (let m = 0; m < len; m++) {
          sliceColors.push(sliceColors[m]);
        }
      }
      let li = "";

      data.forEach((dataValue, i) => {
        li +=
          '<li class="chart_list flex gap-1.5 items-center"><span class="block w-3 h-3 rounded-full" style="background:' +
          sliceColors[i] +
          ';"></span><span class="text-xs leading-5 font-bold cursor-pointer text-slate-800">' +
          dataValue[0] +
          "</span></li>";
      });
      let divBox =
        '<div class="chart_ul_box relative h-full overflow-y-hidden">' +
        "<ul class='absolute top-0 duration-200'>" +
        li +
        "</ul>" +
        "</div>";

      container.parentElement
        .querySelector(".chart_div")
        .insertAdjacentHTML("afterbegin", divBox);

      let max = 0;
      let length =
        container.parentElement.querySelectorAll(".chart_list").length;
      if (length > 8) {
        max = Math.ceil(length / 8);
      } else {
        container.parentElement
          .querySelectorAll(".chart_paging")
          .forEach((el) => el.classList.add("hidden"));
        if (!this.isMobileValue) {
          container.parentElement
            .querySelector(".chart_div")
            .classList.add(`h-[${length * 21}px]`, "pb-0");
        }
      }
      container.parentElement.querySelector(
        ".chart_paging .total_num"
      ).innerHTML = max;
    } else {
      container.parentElement
        .querySelector(".chart_div")
        .classList.add("hidden");
      container
        .closest(".detailed_info_chart")
        .querySelector("h2")
        .classList.add("hidden");
      container.querySelector(".loading").classList.add("hidden");
    }

    this.chart = Highcharts.chart(container, {
      chart: {
        plotShadow: false,
        spacing: [20, 130, 20, 0],
        width: this.widthValue,
        height: this.heightValue,
      },
      credits: {
        enabled: false,
      },
      colors: sliceColors,
      title: {
        text: "",
      },
      tooltip: {
        useHTML: true,
        formatter: function() {
          let originalData = data.find(item => item[0] === this.point.name);
          let PercentageＶalue = originalData ? originalData[1] : this.point.percentage;

          return `<b>${this.point.name}</b><br/>
                  ${parseFloat(PercentageＶalue).toFixed(2)}%`;

        },
      },
      legend: {
        enabled: false,
        width: 140,
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
        y: 6,
        itemMarginTop: 1,
      },
      plotOptions: {
        pie: {
          size: 140,
          allowPointSelect: false,
          allowOverlap: false,
          cursor: "pointer",
          animation: true,
          dataLabels: {
            enabled: false,
          },
          showInLegend: true,
        },
        series: {
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      series: [
        {
          type: "pie",
          data: data,
        },
      ],
    });
  }

  chartSwiper(e) {
    let chartDivTarget = e.target.parentElement.parentElement;
    let ul = chartDivTarget.querySelector("ul");
    let step = parseInt(ul.offsetTop);
    let length = ul.querySelectorAll("li").length;
    let curPage = parseInt(chartDivTarget.querySelector(".chart_paging .cur_num").innerHTML);
    let direction = parseInt(e.target.dataset.indexValue);
    if (length <= 8) return;
    let maxPages = Math.ceil(length / 8);
    let disableBtns = e.target.parentNode.querySelector(".gray")
    if (disableBtns) { disableBtns.classList.remove("gray") }
    if (direction > 0) {
      if (curPage > 1) {
        ul.style.top = step + direction * 168 + "px";
        curPage--;
        if (curPage > 1) {
          e.target.classList.remove("gray");
        } else {
          e.target.classList.add("gray");
        }
        chartDivTarget.querySelector(".chart_paging .cur_num").innerHTML = curPage;
      } else if (curPage == 1) {
        e.target.classList.add("gray");
        chartDivTarget.querySelector(".chart_paging .cur_num").innerHTML = curPage;
      }
    } else {
      if (curPage < maxPages) {
        ul.style.top = step + direction * 168 + "px";
        curPage++;
        if (curPage < maxPages) {
          e.target.classList.remove("gray");
        } else {
          e.target.classList.add("gray");
        }
        chartDivTarget.querySelector(".chart_paging .cur_num").innerHTML = curPage;
      } else if (curPage == maxPages) {
        curPage = maxPages;
        e.target.classList.add("gray");
        chartDivTarget.querySelector(".chart_paging .cur_num").innerHTML = curPage;
      }
    }
  }

  dealTable(container, data) {
    let table_html = "";
    let total_val_cur = 0;
    data.forEach((e) => {
      total_val_cur += e[1];
    });

    data.forEach((e, idx) => {
      let cur_val = e[1];
      let data = this.dealChange(e[2]);
      data.change = isNaN(data.change)
        ? data.change
        : this.toThousandsAndFixed(data.change, 2);
      let hide = idx > 5 ? "hidden" : "";
      let disabled = e[3] ? "" : "opacity-40 point-event-none ";
      table_html +=
        '<tr class="even:bg-slate-50 ' +
        hide +
        '">' +
        "<td>" +
        e[0] +
        "</td>" +
        '<td class="text-end whitespace-nowrap ">' +
        this.toThousandsAndFixed(cur_val, 2) +
        "%</td>" +
        '<td class="text-end whitespace-nowrap ' +
        disabled +
        data.klass +
        '">' +
        data.symbol +
        " " +
        data.change +
        data.percent +
        "</td>" +
        "</tr>";
    });
    container
      .querySelector("tbody")
      .insertAdjacentHTML("beforeend", table_html);

    if (data.length >= 5) {
      let trList = container.querySelectorAll("tr");
      trList.length > 4
        ? container.parentElement
            .querySelector("#" + container.id + "_more")
            .classList.remove("hidden")
        : container.parentElement
            .querySelector("#" + container.id + "_more")
            .classList.add("hidden");
      trList.forEach((tr, idx) => {
        if (idx > 5) {
          tr.classList.add("hidden");
        }
      });
    }
  }

  tableMoreButton(e) {
    let trList = document.querySelectorAll(
      "#" + e.target.dataset.targetValue + " tbody tr"
    );

    count++;
    let x = 5;
    x = x * count + 5 <= trList.length ? x * count + 5 : trList.length;

    trList.forEach((tr, idx) => {
      if (idx <= x) {
        tr.classList.remove("hidden");
      }
    });

    let hide_count_tr = document.querySelectorAll(
      "#" + e.target.dataset.targetValue + " tbody tr .hidden"
    ).length;
    if (hide_count_tr <= 0) {
      document
        .querySelector("#" + e.target.dataset.targetValue + "_more")
        .classList.add("hidden");
    }
  }

  dealChange(change) {
    let klass = "";
    let symbol = "";
    let percent = "";

    if (change === undefined) change = "-";

    if (isNaN(change)) {
      if (change === "---") percent = "%";
      return { change: change, klass: klass, symbol: symbol, percent: percent };
    }
    if (change > 0) {
      klass = " !text-red-700 ";
      symbol = "+";
      percent = "%";
    } else {
      klass = " !text-MK-fund-price-negative ";
      symbol = "-";
      percent = "%";
    }
    return {
      change: Math.abs(change),
      klass: klass,
      symbol: symbol,
      percent: percent,
    };
  }

  toThousandsAndFixed(num, n) {
    let str = this.toThousands(num, n);
    str = str.toString();
    let rightStr = str.split(".")[1];
    if (!rightStr) rightStr = "";
    if (n > 0 && rightStr.length < n) {
      let leftStr = str.split(".")[0];
      let i = rightStr.length;
      while (i++ < n) rightStr = rightStr + "0";
      return leftStr + "." + rightStr;
    } else {
      return str;
    }
  }

  toThousands(num, n) {
    if (!num) {
      return num;
    }
    let plus = num.toString().indexOf("+") != -1 ? "+" : "";
    num =
      num.toString().indexOf("+") != -1 ? num.toString().split("+")[1] : num;
    let leftNum =
      num.toString().indexOf(".") != -1 ? num.toString().split(".")[0] : num;
    let rightStr = "";
    let tmpNum = num.toString();
    if (n >= 0) {
      tmpNum = this.myRound(parseFloat(num), n);
    }
    let rightNum = tmpNum.toString().split(".")[1];

    if (tmpNum > 0 && tmpNum.toString().split(".")[0] > parseInt(leftNum)) {
      leftNum = (parseInt(leftNum) + 1).toString();
    } else if (
      tmpNum < 0 &&
      tmpNum.toString().split(".")[0] < parseInt(leftNum)
    ) {
      leftNum = (parseInt(leftNum) - 1).toString();
    }

    let leftStr = leftNum.toString().replace(/(\d)(?=(?:\d{3})+$)/g, "$1,");

    if (rightNum) {
      rightStr = "." + rightNum;
    }

    return plus + leftStr + rightStr;
  }

  myRound(num, n) {
    let tmp = new Decimal(Number(num))
      .mul(new Decimal(Math.pow(10, n)))
      .toNumber();
    return new Decimal(tmp)
      .round()
      .div(new Decimal(Math.pow(10, n)))
      .toNumber();
  }

  openMemberModal() {
    this.detailedInfoMemberModalTarget.classList.remove("hidden");

    // Modal Swiper
    this.swiper = new Swiper(this.detailedInfoMemberModalTarget.querySelector('.swiper'), {
      modules: [Navigation, Pagination, EffectFade, Autoplay],
      loop: true,
      slidesPerView: 1,
      pagination: {
        el: ".swiper-pagination",
      },
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
      autoplay: {
        delay: 1500,
        disableOnInteraction: false,
      },
      effect: "fade",
    });
  }

  hiddenMemberModal() {
    this.detailedInfoMemberModalTarget.classList.add("hidden");
  }
}
